import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Globe,
  Lock,
  MessageCircle,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Plus,
  RotateCcw,
  Send,
  Smartphone,
  Wallet as WalletIcon,
  X,
} from "lucide-react";
import { ConversationLoader } from "../OlliLogo";
import { openRazorpayCheckout } from "../../../utils/razorpayLoader";
import { NO_DATA } from "../../lib/format";
import { apiPost, dateTime, money, signed } from "./api";

// Design: components/wallet/parts.tsx.

/** Brand glass: only on top of the morning gradient. Labels and numbers, no long text. */
export const glass = "rounded-lg border border-paper/70 bg-paper/55 shadow-soft backdrop-blur-xl";
/** Brand paper: lists, tables and forms. */
export const paper = "rounded-lg bg-paper shadow-soft";

export function IconChip({ children, className = "bg-paper" }) {
  return <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${className}`}>{children}</span>;
}

/** Round sky button that opens the detail behind a card. Ink arrow: paper on sky can't be read. */
export function ArrowLink({ to, label }) {
  return (
    <Link to={to} aria-label={label} title={label} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky text-ink transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:brightness-95">
      <ArrowUpRight size={18} strokeWidth={1.75} />
    </Link>
  );
}

/** Counts a number up when it first shows or changes. Text only, no transform. */
export function useCountUp(value, ms = 800) {
  const [shown, setShown] = useState(value);
  const from = useRef(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(value);
      return;
    }
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / ms);
      const e = 1 - Math.pow(1 - p, 3);
      setShown(a + (value - a) * e);
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return shown;
}

/* ---------- Transaction row parts, shared by the wallet and history ---------- */

/** Recharge green, refund sun, deduction red. Every row also carries its type in words. */
export const TYPE_CHIP = {
  recharge: "bg-success-soft text-success",
  refund: "bg-sun text-ink ring-1 ring-ink/10",
  deduction: "bg-danger-soft text-danger",
};
export const typeChip = (t) => (t.type === "refund" ? TYPE_CHIP.refund : t.credit ? TYPE_CHIP.recharge : TYPE_CHIP.deduction);
// Bonus and adjustment are wallet types the backend also has.
const TYPE_LABEL = { deduction: "Deduction", refund: "Refund", recharge: "Recharge", bonus: "Bonus", adjustment: "Adjustment" };
export const typeLabel = (t) => TYPE_LABEL[t.type] || (t.type ? t.type.charAt(0).toUpperCase() + t.type.slice(1) : NO_DATA);

export function txnIcon(t, size = 16) {
  const p = { size, strokeWidth: 1.75 };
  if (t.kind === "credit") return <Plus {...p} />;
  if (t.kind === "refund") return <RotateCcw {...p} />;
  if (t.kind === "whatsapp" || t.kind === "ai") return <MessageCircle {...p} />;
  if (t.kind === "web") return <Globe {...p} />;
  if (t.kind === "call-in") return <PhoneIncoming {...p} />;
  if (t.kind === "call-out") return <PhoneOutgoing {...p} />;
  if (t.kind === "call" || t.kind === "rental") return <Phone {...p} />;
  return <Send {...p} />;
}

export function Amount({ t, className = "" }) {
  return <span className={`font-semibold tabular-nums ${t.amount > 0 ? "text-success" : "text-ink"} ${className}`}>{signed(t.amount)}</span>;
}

/** `fresh`: a row that arrived with a recharge made in this session glows sun once. */
export function TxnRow({ t, i = 0, fresh = false }) {
  return (
    <li
      style={{ animationDelay: `${i * 40}ms` }}
      className={`-mx-3 flex animate-fade-in items-center gap-4 rounded-md border-t border-line px-3 py-3.5 first:border-t-0 ${fresh ? "animate-row-flash" : ""}`}
    >
      <IconChip className={typeChip(t)}>{txnIcon(t)}</IconChip>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-small font-semibold">{t.title}</span>
        <span className="truncate text-caption text-muted">
          {typeLabel(t)}{t.detail ? ` · ${t.detail}` : ""}
        </span>
      </div>
      <div className="flex flex-col items-end">
        <Amount t={t} className="text-small" />
        <span className="text-caption text-muted">{dateTime(t.ts)}</span>
      </div>
    </li>
  );
}

/* ---------- Pagination (design: components/crm/ui.tsx) ---------- */

export const PAGE_SIZES = [25, 50, 100];

export function usePagination(total, resetKey, initialSize = 25) {
  const [page, setPage] = useState(1);
  const [size, setSizeState] = useState(initialSize);
  const pages = Math.max(1, Math.ceil(total / size));
  useEffect(() => setPage(1), [resetKey]);
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);
  const current = Math.min(page, pages);
  return {
    page: current,
    pages,
    size,
    start: (current - 1) * size,
    end: Math.min(total, current * size),
    total,
    setPage,
    setSize: (n) => {
      setSizeState(n);
      setPage(1);
    },
  };
}

function pageList(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pages - 1, page + 1);
  if (from > 2) out.push("gap");
  for (let i = from; i <= to; i++) out.push(i);
  if (to < pages - 1) out.push("gap");
  out.push(pages);
  return out;
}

/** "1 to 25 of 164 transactions" · rows per page · page buttons. Scrolls the list back into view on page change. */
export function Pagination({ state, noun, plural, scrollTo }) {
  const { page, pages, size, start, end, total, setPage, setSize } = state;
  if (total === 0) return null;
  const go = (p) => {
    setPage(p);
    const el = scrollTo?.current;
    if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  const btn = "flex h-9 min-w-9 items-center justify-center rounded-full px-2 text-small tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ink/20";
  return (
    <nav aria-label="Pagination" className="flex flex-wrap items-center justify-between gap-3 px-1 py-2 text-small">
      <div className="flex items-center gap-4 text-muted">
        <span className="tabular-nums">
          <span className="font-semibold text-ink">
            {(start + 1).toLocaleString("en-IN")} to {end.toLocaleString("en-IN")}
          </span>{" "}
          of {total.toLocaleString("en-IN")} {total === 1 ? noun : plural ?? `${noun}s`}
        </span>
        <label className="flex items-center gap-2">
          Rows per page
          <select
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
            className="h-9 rounded-full bg-paper pl-3 pr-2 text-small font-medium text-ink outline-none ring-1 ring-line hover:ring-muted/50 focus-visible:ring-2 focus-visible:ring-ink/20"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>
      {pages > 1 && (
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => go(page - 1)} disabled={page === 1} aria-label="Previous page" className={`${btn} text-ink hover:bg-paper disabled:pointer-events-none disabled:opacity-35`}>
            <ChevronLeft size={16} strokeWidth={1.75} />
          </button>
          {pageList(page, pages).map((p, i) =>
            p === "gap" ? (
              <span key={`g${i}`} className="px-1 text-muted" aria-hidden>
                ...
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => go(p)}
                aria-current={p === page ? "page" : undefined}
                aria-label={`Page ${p}`}
                className={`${btn} ${p === page ? "bg-ink font-semibold text-paper" : "text-ink hover:bg-paper"}`}
              >
                {p}
              </button>
            ),
          )}
          <button type="button" onClick={() => go(page + 1)} disabled={page === pages} aria-label="Next page" className={`${btn} text-ink hover:bg-paper disabled:pointer-events-none disabled:opacity-35`}>
            <ChevronRight size={16} strokeWidth={1.75} />
          </button>
        </div>
      )}
    </nav>
  );
}

/* ---------- Recharge: amount and tax, then the payment gateway, then done ---------- */

const PRESETS = [25, 50, 100, 250];

// The method picked here is pre-selected in Razorpay Checkout (prefill.method);
// the details themselves are entered in Razorpay's own window, never here.
const IN_METHODS = [
  { id: "upi", label: "UPI", hint: "Any UPI app", icon: <Smartphone size={18} strokeWidth={1.75} /> },
  { id: "card", label: "Card", hint: "Debit or credit", icon: <CreditCard size={18} strokeWidth={1.75} /> },
  { id: "netbanking", label: "Netbanking", hint: "All major banks", icon: <Building2 size={18} strokeWidth={1.75} /> },
  { id: "wallet", label: "Wallets", hint: "Mobile wallets", icon: <WalletIcon size={18} strokeWidth={1.75} /> },
];
const INTL_METHODS = [{ id: "card", label: "Card", hint: "Visa, Mastercard, Amex", icon: <CreditCard size={18} strokeWidth={1.75} /> }];
const METHOD_NOTE = {
  upi: "You'll enter your UPI ID in the secure Razorpay window, then approve the request in your UPI app.",
  card: "You'll enter your card details in the secure Razorpay window.",
  netbanking: "You'll choose your bank in the secure Razorpay window.",
  wallet: "You'll be sent to your wallet app to approve the payment.",
};

function Line({ label, value, strong, muted }) {
  return (
    <div className={`flex items-center justify-between gap-4 ${strong ? "text-body font-semibold" : "text-small"}`}>
      <span className={muted ? "text-muted" : ""}>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

/**
 * Real recharge: POST /api/payments/initiate → Razorpay Checkout →
 * POST /api/payments/razorpay/verify (the same flow as the old RechargeModal).
 * `billing` = { india, gstRate, gstin, legalName, country } or null when unknown;
 * `limits` = { min, max } from GET /api/waba/wallet.
 */
export function RechargeDialog({ open, balance, daily, billing, limits, onClose, onPaid, onPending }) {
  const [step, setStep] = useState("amount");
  const [preset, setPreset] = useState(50);
  const [other, setOther] = useState("");
  const [error, setError] = useState("");
  const [method, setMethod] = useState("upi");
  const [payError, setPayError] = useState("");
  const [paying, setPaying] = useState(false);
  const [newBalance, setNewBalance] = useState(null);

  const methods = billing && !billing.india ? INTL_METHODS : IN_METHODS;
  const MIN = limits?.min ?? 5;
  const MAX = limits?.max ?? 1000;

  useEffect(() => {
    if (!open) return;
    setStep("amount");
    setPreset(50);
    setOther("");
    setError("");
    setMethod(methods[0].id);
    setPayError("");
    setPaying(false);
    setNewBalance(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && !paying && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, paying, onClose]);

  if (!open) return null;

  const amount = preset === "other" ? Number(other.replace(/[^0-9.]/g, "")) || 0 : preset;
  // Same rounding as the backend's /payments/calculate-gst and /payments/initiate.
  const gst = !billing ? null : billing.india ? Math.round(amount * (billing.gstRate / 100) * 100) / 100 : 0;
  const total = gst === null ? null : amount + gst;
  const after = balance === null ? null : balance + amount;
  const days = daily > 0 && after !== null ? Math.floor(after / daily) : 0;

  const toGateway = (e) => {
    e.preventDefault();
    if (amount < MIN || amount > MAX) {
      setError(`Enter an amount between ${money(MIN)} and ${money(MAX)}`);
      return;
    }
    setStep("gateway");
  };

  const pay = async (e) => {
    e.preventDefault();
    setPaying(true);
    setPayError("");
    try {
      const res = await apiPost("/api/payments/initiate", { amount });
      if (!res?.success || !res.data?.razorpayOrderId) throw new Error(res?.message || "Failed to initiate payment");
      const order = res.data;
      let response;
      try {
        response = await openRazorpayCheckout(
          { ...order, prefill: { ...(order.prefill || {}), method } },
          { name: "Olli", description: `Wallet recharge — ${money(order.amountUsd ?? amount)}` },
        );
      } catch (err) {
        if (err.dismissed) {
          setPayError("Payment was cancelled");
          return;
        }
        throw err;
      }
      // Razorpay returned a signature, so the payment went through; the server
      // verifies it, and its webhook credits the wallet if this call never lands.
      let verify;
      try {
        verify = await apiPost("/api/payments/razorpay/verify", {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
      } catch {
        setPayError("Payment received — we are still confirming it. Your balance will update in a minute.");
        onPending?.();
        return;
      }
      if (!verify?.success) throw new Error(verify?.message || "We could not verify the payment. Please contact support.");
      const nb = Number(verify.data?.newBalance);
      setNewBalance(Number.isFinite(nb) ? nb : null);
      onPaid(amount, gst || 0);
      setStep("done");
    } catch (err) {
      setPayError(err.response?.data?.message || err.message || "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  const close = () => !paying && onClose();

  return (
    <div className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-ink/35 p-4" onMouseDown={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="recharge-title"
        onMouseDown={(e) => e.stopPropagation()}
        className="flex max-h-[calc(100vh-32px)] w-[520px] max-w-full animate-nav-in flex-col overflow-hidden rounded-xl bg-paper shadow-lift"
      >
        {/* Progress: three steps, sun fills as you go */}
        <div className="flex gap-1.5 px-7 pt-6" aria-hidden>
          {["amount", "gateway", "done"].map((s, i) => (
            <span key={s} className="h-1 flex-1 overflow-hidden rounded-full bg-mist">
              <span
                className={`block h-full origin-left rounded-full bg-ink transition-transform duration-300 ease-out ${
                  i <= ["amount", "gateway", "done"].indexOf(step) ? "scale-x-100" : "scale-x-0"
                }`}
              />
            </span>
          ))}
        </div>

        {step === "amount" && (
          <form key="amount" onSubmit={toGateway} className="flex animate-fade-in flex-col gap-6 overflow-y-auto p-7">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-1">
                <h2 id="recharge-title" className="text-h3 font-semibold">
                  Recharge wallet
                </h2>
                <p className="text-small text-muted">Current balance {money(balance)}</p>
              </div>
              <CloseButton onClick={close} />
            </div>

            <fieldset className="flex flex-col gap-3">
              <legend className="mb-3 text-small font-medium">Amount to add</legend>
              <div className="grid grid-cols-4 gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={preset === p}
                    onClick={() => {
                      setPreset(p);
                      setError("");
                    }}
                    className={`relative h-14 rounded-lg text-body font-semibold tabular-nums transition-colors duration-150 ${
                      preset === p ? "bg-ink text-paper" : "bg-mist text-ink hover:bg-line"
                    }`}
                  >
                    ${p}
                    {p === 100 && <span className={`absolute -top-2 right-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${preset === p ? "bg-sun text-ink" : "bg-ink text-paper"}`}>Popular</span>}
                  </button>
                ))}
              </div>
              <div
                className={`flex h-12 items-center rounded-full border bg-paper px-5 focus-within:border-ink focus-within:ring-2 focus-within:ring-ink/10 ${
                  error ? "border-danger" : preset === "other" ? "border-ink" : "border-line"
                }`}
              >
                <span className="text-body text-muted">$</span>
                <input
                  inputMode="decimal"
                  aria-label="Other amount"
                  aria-invalid={!!error || undefined}
                  placeholder={`Other amount, ${money(MIN)} to ${money(MAX)}`}
                  value={other}
                  onFocus={() => setPreset("other")}
                  onChange={(e) => {
                    setOther(e.target.value);
                    setPreset("other");
                    setError("");
                  }}
                  className="h-full flex-1 bg-transparent pl-1 text-body tabular-nums outline-none placeholder:text-muted/70"
                />
              </div>
              {error && <p className="animate-fade-in text-caption font-medium text-danger">{error}</p>}
            </fieldset>

            <div className="flex flex-col gap-3 rounded-lg bg-mist p-5">
              <Line label="Added to wallet" value={money(amount)} />
              {!billing ? (
                <Line label="GST" value={NO_DATA} muted />
              ) : billing.india ? (
                <Line label={`GST ${billing.gstRate}%`} value={money(gst)} muted />
              ) : (
                <Line label="GST" value="Not applicable" muted />
              )}
              <div className="border-t border-line pt-3">
                <Line label="Total payable" value={money(total)} strong />
              </div>
              {billing && (
                <p className="text-caption text-muted">
                  {billing.india
                    ? billing.gstin
                      ? `Billed to ${billing.legalName}, GSTIN ${billing.gstin}`
                      : `Billed to ${billing.legalName}. Add your GSTIN to claim input tax credit.`
                    : `Billed to ${billing.legalName}, ${billing.country}. No GST outside India.`}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between text-small">
              <span className="text-muted">Balance after recharge</span>
              <span className="font-semibold tabular-nums">
                {money(after)}
                {amount > 0 && daily > 0 && after !== null && <span className="font-normal text-muted"> · about {days} days</span>}
              </span>
            </div>

            <button type="submit" className="flex h-12 items-center justify-center gap-2 rounded-full bg-sun text-body font-semibold text-ink transition-[filter] hover:brightness-95">
              Continue to payment · {money(total)}
            </button>
          </form>
        )}

        {step === "gateway" && (
          <form key="gateway" onSubmit={pay} className="flex animate-fade-in flex-col gap-6 overflow-y-auto p-7">
            <div className="flex items-start justify-between gap-4">
              <button
                type="button"
                disabled={paying}
                onClick={() => setStep("amount")}
                className="flex items-center gap-1.5 text-small font-medium text-muted hover:text-ink disabled:opacity-50"
              >
                <ArrowLeft size={16} strokeWidth={1.75} />
                Change amount
              </button>
              <CloseButton onClick={close} />
            </div>

            <div className="flex items-end justify-between gap-4">
              <div className="flex flex-col gap-1">
                <h2 id="recharge-title" className="text-h3 font-semibold">
                  Choose how to pay
                </h2>
                <p className="flex items-center gap-1.5 text-caption text-muted">
                  <Lock size={12} strokeWidth={2} />
                  Secure payment via Razorpay · paying Olli
                </p>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-caption text-muted">Payable</span>
                <span className="text-h3 font-semibold tabular-nums">{money(total)}</span>
              </div>
            </div>

            <div role="radiogroup" aria-label="Payment method" className={`grid gap-2 ${methods.length === 4 ? "grid-cols-2" : "grid-cols-3"}`}>
              {methods.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  role="radio"
                  aria-checked={method === m.id}
                  disabled={paying}
                  onClick={() => {
                    setMethod(m.id);
                    setPayError("");
                  }}
                  className={`flex items-center gap-3 rounded-lg border p-3.5 text-left transition-colors duration-150 ${
                    method === m.id ? "border-ink bg-paper" : "border-line hover:border-muted/50"
                  }`}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${method === m.id ? "bg-ink text-paper" : "bg-mist text-ink"}`}>{m.icon}</span>
                  <span className="flex min-w-0 flex-col">
                    <span className="text-small font-semibold">{m.label}</span>
                    <span className="truncate text-caption text-muted">{m.hint}</span>
                  </span>
                </button>
              ))}
            </div>

            <div key={method} className="flex min-h-[124px] animate-fade-in flex-col gap-3">
              <p className="rounded-lg bg-mist p-4 text-small text-muted">{METHOD_NOTE[method]}</p>
              {payError && <p className="animate-fade-in text-caption font-medium text-danger">{payError}</p>}
            </div>

            <button
              type="submit"
              disabled={paying}
              className="flex h-12 items-center justify-center gap-2 rounded-full bg-sun text-body font-semibold text-ink transition-[filter] hover:brightness-95 disabled:cursor-wait"
            >
              {paying ? (
                <>
                  <ConversationLoader size={20} tone="white" />
                  Confirming payment…
                </>
              ) : (
                `Pay ${money(total)}`
              )}
            </button>
          </form>
        )}

        {step === "done" && (
          <div key="done" className="flex animate-fade-in flex-col items-center gap-5 p-7 pb-8 pt-10 text-center">
            <span className="flex h-16 w-16 animate-olli-pop items-center justify-center rounded-full bg-success-soft text-success">
              <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 12.5l4.5 4.5L19 7.5" strokeDasharray="24" className="animate-check-draw" />
              </svg>
            </span>
            <div className="flex flex-col gap-1">
              <h2 id="recharge-title" className="text-h3 font-semibold">
                {money(amount)} added to your wallet
              </h2>
              <p className="text-small text-muted">
                New balance <span className="font-semibold tabular-nums text-ink">{money(newBalance)}</span>
                {daily > 0 && newBalance !== null && ` · about ${Math.floor(newBalance / daily)} days`}
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 rounded-lg bg-mist p-4 text-left">
              <Line label="Paid" value={money(total)} />
              {gst > 0 && <Line label="Includes GST" value={money(gst)} muted />}
            </div>
            <button onClick={onClose} className="flex h-12 w-full items-center justify-center rounded-full bg-ink text-body font-semibold text-paper hover:bg-ink/85">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CloseButton({ onClick }) {
  return (
    <button type="button" onClick={onClick} aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink">
      <X size={18} strokeWidth={1.75} />
    </button>
  );
}

export function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [message, onDone]);
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center" role="status">
      <div className="pointer-events-auto flex h-12 animate-nav-in items-center gap-3 rounded-full bg-ink pl-4 pr-5 text-small font-medium text-paper shadow-lift">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sun text-ink">
          <Check size={14} strokeWidth={2.25} />
        </span>
        {message}
      </div>
    </div>
  );
}
